import { isNil } from "lopermut"
import { Check, ChevronDown, Plus, X } from "lucide-react"
import { useState } from "react"
import { Button } from "../atoms/Button"
import { ButtonGroup } from "../atoms/ButtonGroup"
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/Popover"
import { cn } from "../utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./DropdownMenu"
import { FilterBarLabels } from "./FilterBar"

const SEARCH_THRESHOLD = 8

export type FilterMenuField = {
  key: string
  label: string
  value: string | undefined
  onChange: (value: string | undefined) => void
  options: readonly { value: string; label: string; className?: string }[]
  condition?: boolean
}

type Props = {
  fields: FilterMenuField[]
  labels: FilterBarLabels
  className?: string
}

export function FilterMenu({ fields, labels, className }: Props) {
  const visibleFields = fields.filter((field) => field.condition !== false)
  const activeFields = visibleFields.filter((field) => !isNil(field.value))
  const inactiveFields = visibleFields.filter((field) => isNil(field.value))

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {activeFields.map((field) => (
        <ActiveFilter key={field.key} field={field} labels={labels} />
      ))}
      {inactiveFields.length > 0 && <AddFilter fields={inactiveFields} labels={labels} />}
    </div>
  )
}

type ActiveFilterProps = {
  field: FilterMenuField
  labels: FilterBarLabels
}

function ActiveFilter({ field, labels }: ActiveFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selected = field.options.find((option) => option.value === field.value)

  return (
    <ButtonGroup>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-10 gap-2 px-3">
            <span className="text-muted-foreground">{field.label}</span>
            <span className="max-w-40 truncate">{selected?.label}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-56 p-0" align="start">
          <OptionsList
            field={field}
            labels={labels}
            onSelect={(value) => {
              field.onChange(value)
              setIsOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        className="h-10 w-10 p-0 text-muted-foreground"
        aria-label={labels.removeFilter}
        onClick={() => field.onChange(undefined)}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </ButtonGroup>
  )
}

type AddFilterProps = {
  fields: FilterMenuField[]
  labels: FilterBarLabels
}

function AddFilter({ fields, labels }: AddFilterProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-10 gap-2 px-3">
          <Plus className="h-4 w-4" />
          {labels.addFilter}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        {fields.map((field) => (
          <DropdownMenuSub key={field.key}>
            <DropdownMenuSubTrigger>{field.label}</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                {field.options.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    className={option.className}
                    onClick={() => field.onChange(option.value)}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type OptionsListProps = {
  field: FilterMenuField
  labels: FilterBarLabels
  onSelect: (value: string) => void
}

function OptionsList({ field, labels, onSelect }: OptionsListProps) {
  return (
    <Command>
      {field.options.length > SEARCH_THRESHOLD && <CommandInput placeholder={labels.search} />}
      <CommandList>
        <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
          {labels.empty}
        </CommandEmpty>
        <CommandGroup>
          {field.options.map((option) => (
            <CommandItem
              key={option.value}
              value={`${option.label} ${option.value}`}
              onSelect={() => onSelect(option.value)}
              className={option.className}
            >
              <span className="grow truncate">{option.label}</span>
              <Check
                className={cn("h-3.5 w-3.5", field.value !== option.value && "opacity-0")}
              />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
